import { useState, useEffect, useCallback } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, Menu, X } from "lucide-react";
import { CONTACT, ANNOUNCEMENT } from "../data/site";

const links = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/eyewear", label: "Eyewear" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

const Logo = ({ scrolled }) => (
  <Link to="/" data-testid="logo-link" className="flex flex-col leading-none">
    <span
      className={`font-serif text-2xl font-semibold tracking-tight transition-colors ${
        scrolled ? "text-navy" : "text-white"
      }`}
    >
      Brodheadsville
    </span>
    <span className="text-[11px] uppercase tracking-[0.35em] text-tiffany font-medium">
      Eye Care
    </span>
  </Link>
);

const navLinkColor = (isActive, scrolled) => {
  if (isActive) return "text-tiffany";
  return scrolled ? "text-navy" : "text-white";
};

const DesktopNav = ({ scrolled }) => (
  <nav className="hidden md:flex items-center gap-9">
    {links.map((l) => (
      <NavLink
        key={l.to}
        to={l.to}
        data-testid={`nav-${l.label.toLowerCase()}`}
        className={({ isActive }) =>
          `text-sm font-medium tracking-wide transition-colors hover:text-tiffany ${navLinkColor(
            isActive,
            scrolled
          )}`
        }
      >
        {l.label}
      </NavLink>
    ))}
  </nav>
);

const MobileMenu = () => (
  <div data-testid="mobile-menu" className="md:hidden bg-white border-t border-border">
    <nav className="flex flex-col px-5 py-4">
      {links.map((l) => (
        <NavLink
          key={l.to}
          to={l.to}
          data-testid={`mobile-nav-${l.label.toLowerCase()}`}
          className={({ isActive }) =>
            `py-3 text-base font-medium border-b border-border/60 ${
              isActive ? "text-tiffany" : "text-navy"
            }`
          }
        >
          {l.label}
        </NavLink>
      ))}
      <a
        href={CONTACT.phoneHref}
        data-testid="mobile-call-button"
        className="mt-4 inline-flex items-center justify-center gap-2 bg-tiffany text-white px-5 py-3 rounded-sm font-semibold"
      >
        <Phone size={16} /> Call {CONTACT.phone}
      </a>
    </nav>
  </div>
);

const DISMISS_ICON_SIZE = 15;
const MENU_ICON_SIZE = 24;

const persistAnnouncementDismissal = () => sessionStorage.setItem("announcementDismissed", "1");
const isAnnouncementDismissed = () => sessionStorage.getItem("announcementDismissed") === "1";

const AnnouncementBar = ({ onDismiss }) => (
  <div data-testid="announcement-bar" className="bg-tiffany text-white">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-2 flex items-center justify-center gap-3">
      <p className="text-xs sm:text-sm font-medium text-center">
        {ANNOUNCEMENT.text}{" "}
        <a href={CONTACT.phoneHref} data-testid="announcement-call-link" className="underline underline-offset-2 font-semibold whitespace-nowrap">
          Call {CONTACT.phone}
        </a>
      </p>
      <button
        onClick={onDismiss}
        data-testid="announcement-dismiss"
        aria-label="Dismiss announcement"
        className="shrink-0 hover:opacity-70 transition-opacity"
      >
        <X size={DISMISS_ICON_SIZE} />
      </button>
    </div>
  </div>
);

const HeaderActions = ({ scrolled, open, onToggle }) => (
  <div className="flex items-center gap-3">
    <a
      href={CONTACT.phoneHref}
      data-testid="header-call-button"
      className="hidden sm:inline-flex items-center gap-2 bg-tiffany hover:bg-tiffany-dark text-white px-5 py-2.5 rounded-sm text-sm font-semibold transition-colors active:scale-[0.98]"
    >
      <Phone size={16} />
      {CONTACT.phone}
    </a>
    <button
      data-testid="mobile-menu-toggle"
      className={`md:hidden p-2 transition-colors ${scrolled ? "text-navy" : "text-white"}`}
      onClick={onToggle}
      aria-label="Toggle menu"
    >
      {open ? <X size={MENU_ICON_SIZE} /> : <Menu size={MENU_ICON_SIZE} />}
    </button>
  </div>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [showBanner, setShowBanner] = useState(
    () => ANNOUNCEMENT.enabled && !isAnnouncementDismissed()
  );
  const location = useLocation();

  const dismissBanner = useCallback(() => {
    persistAnnouncementDismissal();
    setShowBanner(false);
  }, [setShowBanner]);

  const onScroll = useCallback(() => setScrolled(window.scrollY > 20), [setScrolled]);

  useEffect(() => {
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [onScroll]);

  useEffect(() => setOpen(false), [location.pathname, setOpen]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-border shadow-sm"
          : "bg-transparent"
      }`}
    >
      {showBanner && <AnnouncementBar onDismiss={dismissBanner} />}
      <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between h-20">
        <Logo scrolled={scrolled} />
        <DesktopNav scrolled={scrolled} />
        <HeaderActions scrolled={scrolled} open={open} onToggle={() => setOpen((v) => !v)} />
      </div>
      {open && <MobileMenu />}
    </header>
  );
};

export default Navbar;
